
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface StandardStatus {
  value: 'Active' | 'Draft' | 'Review' | 'Approved' | 'Published' | 'Deprecated' | 'Retired';
}

interface Requirement {
  id: string;
  identifier: string;
  text: string;
  type?: string;
  level?: string;
}

interface Standard {
  id: string;
  title: string;
  category: string;
  status: StandardStatus | string;
  identifier?: string;
  version?: string;
  scope?: string;
  purpose?: string;
  requirements?: Requirement[];
}

interface ProjectFormProps {
  selectedStandard: Standard;
  onSubmit: (project: any) => void;
  onCancel: () => void;
}

export function ProjectForm({ 
  selectedStandard, 
  onSubmit, 
  onCancel 
}: ProjectFormProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error("Please enter a project title");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      // Get the current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in to create a project");
        return;
      }
      
      // Create the new project
      const projectData = {
        title,
        description: description || null,
        standard_id: selectedStandard.id,
        user_id: user.id,
        status: 'In Progress',
        progress: 0
      };
      
      const { data, error } = await supabase
        .from('projects')
        .insert([projectData])
        .select();
      
      if (error) {
        throw error;
      }
      
      toast.success("Project created successfully!");
      onSubmit(data);
    } catch (error: any) {
      console.error("Error creating project:", error);
      toast.error(error.message || "Failed to create project");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRequirements = () => {
    setShowRequirements(!showRequirements);
  };
  
  const hasRequirements = selectedStandard.requirements && selectedStandard.requirements.length > 0;
  
  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border shadow-lg w-full max-w-md overflow-hidden flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-2xl font-bold">Add New Project</h2>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Project Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter project title"
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">Project Description (Optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter project description"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="standard">Selected Standard</Label>
            <div className="flex flex-col space-y-2 p-3 bg-muted rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{selectedStandard.title}</p>
                  <p className="text-sm text-muted-foreground">{selectedStandard.category}</p>
                </div>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  typeof selectedStandard.status === 'object' 
                    ? (selectedStandard.status.value === 'Active' 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300')
                    : (selectedStandard.status === 'Active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300')
                }`}>
                  {typeof selectedStandard.status === 'object' 
                    ? selectedStandard.status.value 
                    : selectedStandard.status}
                </span>
              </div>
              
              {selectedStandard.identifier && (
                <div className="text-sm">
                  <span className="font-medium">Identifier:</span> {selectedStandard.identifier}
                </div>
              )}
              
              {selectedStandard.version && (
                <div className="text-sm">
                  <span className="font-medium">Version:</span> {selectedStandard.version}
                </div>
              )}
              
              {selectedStandard.scope && (
                <div className="text-sm">
                  <span className="font-medium">Scope:</span> {selectedStandard.scope}
                </div>
              )}
              
              {selectedStandard.purpose && (
                <div className="text-sm text-muted-foreground mt-1 line-clamp-2">
                  {selectedStandard.purpose}
                </div>
              )}

              {hasRequirements && (
                <div className="mt-2 pt-2 border-t border-border">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    className="w-full text-xs"
                    onClick={toggleRequirements}
                  >
                    {showRequirements ? 'Hide' : 'Show'} Requirements ({selectedStandard.requirements?.length})
                  </Button>
                  
                  {showRequirements && (
                    <div className="mt-2 max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {selectedStandard.requirements?.map(req => (
                          <div key={req.id} className="p-2 bg-background rounded border text-sm">
                            <div className="font-medium">{req.identifier}</div>
                            <div className="text-muted-foreground">{req.text}</div>
                            {(req.type || req.level) && (
                              <div className="flex gap-2 mt-1 text-xs">
                                {req.type && (
                                  <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                                    {req.type}
                                  </span>
                                )}
                                {req.level && (
                                  <span className="bg-secondary/10 text-secondary px-1.5 py-0.5 rounded-full">
                                    {req.level}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Project"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
